// ===========================================================================
// ReHome — Google Maps helper (shared across admin + platform)
// ---------------------------------------------------------------------------
// Uses the **new** Places API (`AutocompleteSuggestion` + `Place`) plus the
// Geocoding API. The programmatic suggestion surface is used (not the raw
// <gmp-place-autocomplete> web component) so the address input stays a normal
// controlled React input with a fully custom, stylable dropdown, and the same
// code works on both apps.
//
// Graceful degradation: if VITE_GOOGLE_MAPS_API_KEY is not configured (or the
// key/referrer is rejected, or a request fails), every function resolves to an
// empty/neutral value and callers fall back to plain manual entry. Failures are
// logged server-side (log_client_diagnostic) instead of surfaced to users —
// EXCEPT that they are logged loudly enough to diagnose a blocked key.
//
// Uses `any` for the raw `window.google` surface — this module is the sole
// boundary that touches it (@types/google.maps is not a dependency).
// ===========================================================================

import { logClientDiagnostic } from './mutations';

declare global {
  interface Window {
    google?: any;
  }
}

let loadPromise: Promise<any | null> | null = null;
let hasLoggedMissingKey = false;

export const getApiKey = (): string | undefined => {
  try {
    return (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY || undefined;
  } catch {
    return undefined;
  }
};

export const isGoogleMapsConfigured = (): boolean => !!getApiKey();

/**
 * Telepíti a `google.maps.importLibrary` belépési pontot (Google "Dynamic
 * Library Import" bootstrap), ha még nincs.
 *
 * MIÉRT KELL EZ: a klasszikus
 * `maps/api/js?key=…&libraries=places,geocoding` szkript-URL **nem**
 * definiálja az `importLibrary`-t — az csak a bootstrap loaderrel jön létre.
 * Emiatt a korábbi betöltő minden hívásnál elhasalt
 * ("window.google.maps.importLibrary is not a function"), a `loadGoogleMaps()`
 * mindig `null`-t adott, és így a cím mezők NÉMÁN sima szöveges bevitelre
 * estek vissza — a "Bekerülés és Azonosítás" űrlapon és minden más
 * címmezőnél is. A hiba csak a szerveroldali diagnosztikai logban látszott.
 *
 * A megvalósítás a hivatalos bootstrap kiolvasható változata: a valódi
 * `importLibrary` a szkript betöltése után felülírja az itteni ideiglenest,
 * ezért a `then`-ágban ÚJRA a `maps.importLibrary`-t olvassuk ki.
 */
const installDynamicLoader = (apiKey: string): void => {
  const g: any = (window.google = window.google || {});
  const maps: any = (g.maps = g.maps || {});
  if (maps.importLibrary) return;

  const requested = new Set<string>();
  let scriptPromise: Promise<void> | null = null;
  const CALLBACK = '__rehomeMapsReady__';

  const loadScript = (): Promise<void> => {
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise<void>((resolve, reject) => {
      // Egy mikrotaszkkal később építjük az URL-t, hogy az UGYANEBBEN a
      // tickben indított importLibrary('places') + importLibrary('geocoding')
      // hívások mindegyike bekerüljön a `libraries` paraméterbe (a hivatalos
      // snippet ugyanezt egy `await`-tel éri el).
      queueMicrotask(() => {
        const params = new URLSearchParams({
          key: apiKey,
          v: 'weekly',
          language: 'hu',
          region: 'HU',
          libraries: Array.from(requested).join(','),
          callback: `google.maps.${CALLBACK}`,
        });
        maps[CALLBACK] = () => resolve();
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
        script.async = true;
        script.nonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce || '';
        script.onerror = () =>
          reject(new Error('A Google Maps szkript nem töltődött be (hálózat vagy API-kulcs referrer-korlátozás).'));
        document.head.appendChild(script);
      });
    });
    return scriptPromise;
  };

  maps.importLibrary = (name: string) => {
    requested.add(name);
    // A `.then` idejére a valódi implementáció már a helyén van.
    return loadScript().then(() => maps.importLibrary(name));
  };
};

/**
 * Loads the Google Maps JS API with the `places` + `geocoding` libraries
 * exactly once and resolves with the `google` namespace (or `null` if not
 * configured / failed).
 */
export const loadGoogleMaps = (): Promise<any | null> => {
  if (loadPromise) return loadPromise;

  const apiKey = getApiKey();
  if (!apiKey) {
    if (!hasLoggedMissingKey) {
      hasLoggedMissingKey = true;
      logClientDiagnostic('google_maps', 'VITE_GOOGLE_MAPS_API_KEY nincs beállítva — a cím mező kézi bevitelre esett vissza.');
    }
    loadPromise = Promise.resolve(null);
    return loadPromise;
  }

  installDynamicLoader(apiKey);

  loadPromise = (async () => {
    try {
      await Promise.all([
        window.google!.maps.importLibrary('places'),
        window.google!.maps.importLibrary('geocoding'),
      ]);
      return window.google;
    } catch (err: any) {
      logClientDiagnostic('google_maps', `A Google Maps könyvtárak betöltése sikertelen: ${err?.message ?? err}`);
      return null;
    }
  })();

  return loadPromise;
};

// ─── Address autocomplete (new Places API) ──────────────────────────────────

export interface AddressSuggestion {
  /** Google place id. */
  id: string;
  /** Human-readable suggestion text. */
  text: string;
  /** Opaque prediction — pass to resolveAddressSuggestion() for coordinates. */
  prediction: any;
}

// One billing session spans "type + pick". Reset after a pick (or a cleared
// field) so the next search opens a fresh session.
let sessionToken: any = null;
const getSessionToken = (google: any): any => {
  if (!sessionToken && google?.maps?.places?.AutocompleteSessionToken) {
    sessionToken = new google.maps.places.AutocompleteSessionToken();
  }
  return sessionToken;
};
export const resetAddressSession = (): void => { sessionToken = null; };

/**
 * Only settlements (city / town / village), no streets or house numbers.
 * Az örökbefogadói lakóhelyhez ezt használjuk: a távolság-pontozásnak
 * km-es nagyságrend elég, egy pontos lakcím viszont sokkal érzékenyebb adat
 * lenne (l. 040_shelter_address_and_adopter_lifestyle.sql adattakarékossági
 * megjegyzése).
 */
export const CITY_PLACE_TYPES = ['locality', 'postal_town', 'administrative_area_level_2'];

/**
 * Fetches address suggestions for the given input. Returns [] on any failure
 * so the caller can silently fall back to manual entry.
 *
 * @param opts.includedPrimaryTypes szűkítés hely-típusra (pl. CITY_PLACE_TYPES)
 * @param opts.minLength hány karaktertől kérdezzen (alapértelmezés 3)
 */
export async function fetchAddressSuggestions(
  input: string,
  opts?: { region?: string; includedPrimaryTypes?: string[]; minLength?: number }
): Promise<AddressSuggestion[]> {
  const q = input.trim();
  if (q.length < (opts?.minLength ?? 3)) return [];

  const google = await loadGoogleMaps();
  const AutocompleteSuggestion = google?.maps?.places?.AutocompleteSuggestion;
  if (!AutocompleteSuggestion) return [];

  try {
    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: q,
      includedRegionCodes: [opts?.region ?? 'hu'],
      ...(opts?.includedPrimaryTypes ? { includedPrimaryTypes: opts.includedPrimaryTypes } : {}),
      sessionToken: getSessionToken(google),
    });
    return (suggestions || [])
      .filter((s: any) => s?.placePrediction)
      .map((s: any) => ({
        id: s.placePrediction.placeId,
        text: s.placePrediction.text?.text ?? String(s.placePrediction.text ?? ''),
        prediction: s.placePrediction,
      }));
  } catch (err: any) {
    logClientDiagnostic('google_maps', `Címjavaslat lekérése sikertelen: ${err?.message ?? err}`);
    return [];
  }
}

/** A kiválasztott hely feloldva: cím, koordináták és a Google Place ID. */
export interface ResolvedPlace {
  address: string;
  lat: number;
  lng: number;
  /**
   * Google Place ID. Ez a BIZONYÍTÉK arra, hogy a címet a Google
   * javaslataiból választották, nem kézzel gépelték — a menhelyi cím
   * validálása ezen alapul (shelters.address_place_id).
   */
  placeId: string;
}

/** Resolves a picked suggestion into a formatted address + coordinates. */
export async function resolveAddressSuggestion(
  suggestion: AddressSuggestion
): Promise<ResolvedPlace | null> {
  try {
    const place = suggestion.prediction.toPlace();
    await place.fetchFields({ fields: ['formattedAddress', 'location'] });
    resetAddressSession();
    const loc = place.location;
    if (!loc) return null;
    return {
      address: place.formattedAddress ?? suggestion.text,
      lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
      lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
      placeId: suggestion.id,
    };
  } catch (err: any) {
    logClientDiagnostic('google_maps', `Hely feloldása sikertelen: ${err?.message ?? err}`);
    return null;
  }
}

// ─── Geocoding (GPS ↔ address) ───────────────────────────────────────────────

/** GPS coordinates → formatted address (null on failure). */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  const google = await loadGoogleMaps();
  if (!google?.maps?.Geocoder) return null;
  try {
    const geocoder = new google.maps.Geocoder();
    const { results } = await geocoder.geocode({ location: { lat, lng } });
    return results?.[0]?.formatted_address ?? null;
  } catch (err: any) {
    logClientDiagnostic('google_maps', `Fordított geokódolás sikertelen: ${err?.message ?? err}`);
    return null;
  }
}

/** Address text → GPS coordinates (null on failure). */
export async function forwardGeocode(address: string): Promise<{ lat: number; lng: number } | null> {
  const q = address.trim();
  if (!q) return null;
  const google = await loadGoogleMaps();
  if (!google?.maps?.Geocoder) return null;
  try {
    const geocoder = new google.maps.Geocoder();
    const { results } = await geocoder.geocode({ address: q, region: 'hu' });
    const loc = results?.[0]?.geometry?.location;
    if (!loc) return null;
    return { lat: loc.lat(), lng: loc.lng() };
  } catch (err: any) {
    logClientDiagnostic('google_maps', `Geokódolás sikertelen: ${err?.message ?? err}`);
    return null;
  }
}
